import React, { useState, useEffect } from "react";
import { Button } from "reactstrap";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { getContractByIdFull as onGetContractByIdFull } from "../../../slices/thunks";
import ContractDetailHeader from "./Components/ContractDetailHeader";
import ContractOverdueAlert from "./Components/ContractOverdueAlert";
import ContractMetricsGrid from "./Components/ContractMetricsGrid";
import ClientDossierCard from "./Components/ClientDossierCard";
import ContractUnits from "./Components/ContractUnits";
import ContractTermsCard from "./Components/ContractTermsCard";
import EditContractModal from "./Components/EditContractModal";
import PaymentsLedger from "./Components/PaymentsLedger";
import DocumentList from "./Components/DocumentList";
import { theme } from "./theme";

const selectContractState = (state) => state.Contract;
const selectContractProperties = createSelector(selectContractState, (contract) => ({
  selectedContractFull: contract.selectedContractFull,
  loading: contract.loading,
  error: contract.error,
}));

const ContractDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedContractFull, loading, error } = useSelector(selectContractProperties);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(onGetContractByIdFull(id));
  }, [id, dispatch]);

  if (loading && !selectedContractFull) {
    return (
      <div className="page-content">
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading contract details...</p>
        </div>
      </div>
    );
  }

  if (error && !selectedContractFull) {
    return (
      <div className="page-content">
        <div className="text-center mt-5">
          <div className="alert alert-danger d-inline-block p-4">
            <i className="ri-error-warning-line fs-1 d-block mb-3"></i>
            <h5 className="mb-3">Failed to load contract details</h5>
            <p className="mb-4">{error}</p>
            <Button color="primary" onClick={() => dispatch(onGetContractByIdFull(id))}>
              <i className="ri-refresh-line me-1"></i> Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ background: "#f4f4f6" }}>
      <div style={{ maxWidth: "100%", margin: "0 auto", padding: "4px 80px" }}>
        <ContractDetailHeader
          contract={selectedContractFull}
          onEditClick={() => setEditModalOpen(true)}
        />

        <ContractOverdueAlert contract={selectedContractFull} />

        <ContractMetricsGrid contract={selectedContractFull} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 24, flex: "1 1 660px", minWidth: "min(100%, 580px)" }}>
            <div style={{ display: "flex", flexDirection: "column", maxHeight: '100%', overflow: "hidden" }}>
              <PaymentsLedger contract={selectedContractFull} />
            </div>

            <ContractUnits units={selectedContractFull?.units || []} contract={selectedContractFull} />

            <ContractTermsCard contract={selectedContractFull} />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
              flex: "1 1 348px",
              minWidth: "min(100%, 320px)",
              maxWidth: "100%",
              alignSelf: "flex-start",
              position: "sticky",
              top: 24,
              background: theme.tray,
              border: `1px solid ${theme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <ClientDossierCard client={selectedContractFull?.client} />

            <div style={{ display: "flex", flexDirection: "column", maxHeight: 420, overflow: "hidden" }}>
              <DocumentList contract={selectedContractFull} />
            </div>
          </div>
        </div>
      </div>

      <EditContractModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen((prev) => !prev)}
        contract={selectedContractFull}
      />
    </div>
  );
};

export default ContractDetail;
